/** Kelly AI conversations side panel: the docked aside with the New chat button, search, and thread list. */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import type { ChangeEvent, KeyboardEvent, Ref } from "react"
import { Add, Clear, ExpandMore, KeyboardDoubleArrowLeft, Search } from "@mui/icons-material"
import { Alert, Box, Button, CircularProgress, Collapse, IconButton, InputAdornment, List, ListSubheader, Paper, TextField, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import type { ThreadListSurface } from "connected-components/ai-teammate/analytics/events"
import type { Thread } from "connected-components/ai-teammate/types/thread"
import { useAnalytics } from "connected-components/analytics"
import { ThreadListItem, ThreadListItemPreview, type ThreadRowCallbacks } from "./thread-list-item"
import { filterThreadsByQuery } from "./thread-filter"
import { partitionThreads } from "./thread-partition"
import { useLatest } from "./use-latest"

/** Which list a thread was opened from. "rail_pin" joins the list with rail pins (ADR 0012). */
export type ThreadOpenSource = "recents" | "pinned"

// Shared with the collapsed rail's hover preview (collapsed-rail.tsx) so the docked panel and its floating peek stay the same width.
export const PANEL_WIDTH = "22rem"
// min(…, 100%): a no-op in page mode (the area is always wider), and the dock's slide-over fills a 320px dock.
const PANEL_SX = { width: `min(${PANEL_WIDTH}, 100%)`, flexShrink: 0, borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column", minHeight: 0 } satisfies SxProps<Theme>
// The collapse control sits at the panel's top-right (APP-3397); the open affordance is page-local (a floating button over the chat area).
const COLLAPSE_ROW_SX = { display: "flex", justifyContent: "flex-end", p: 0.5 } satisfies SxProps<Theme>
const CONTENT_SX = { display: "flex", flexDirection: "column", minHeight: 0, flex: 1 } satisfies SxProps<Theme>
const NEW_CHAT_SX = { justifyContent: "flex-start", textTransform: "none", m: 1 } satisfies SxProps<Theme>
const NEW_CHAT_ACTIVE_SX = { ...NEW_CHAT_SX, bgcolor: "action.selected" } satisfies SxProps<Theme>
const LISTS_SCROLL_SX = { flex: 1, overflowY: "auto", minHeight: 0 } satisfies SxProps<Theme>
const ALERT_SX = { m: 1 } satisfies SxProps<Theme>
// Legacy header line-height, not the default 48px ListSubheader lane.
const SUBHEADER_SX = { lineHeight: 2, fontWeight: 600 } satisfies SxProps<Theme>
// Collapsible-section header: a real button (aria-expanded) styled to read like the static ListSubheader.
const SECTION_TOGGLE_SX = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5,
  width: "100%", px: 2, py: 0, border: 0, background: "none", cursor: "pointer",
  font: "inherit", lineHeight: 2, fontWeight: 600, textAlign: "left",
  color: "var(--app-text-secondary)",
  "&:hover": { bgcolor: "action.hover" },
} satisfies SxProps<Theme>
const CHEVRON_SX = { fontSize: "1.25rem", transition: "transform 150ms ease" } satisfies SxProps<Theme>
const SEARCH_WRAP_SX = { px: 1, pb: 0.5 } satisfies SxProps<Theme>
// Quiet, rounded input in the panel's own language: subtle divider border, paper fill, ~6px radius like the rows.
const SEARCH_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "var(--app-bg-paper)",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "var(--app-divider)" },
    "&:hover fieldset": { borderColor: "var(--app-divider)" },
    "&.Mui-focused fieldset": { borderColor: "var(--app-primary)", borderWidth: "1px" },
  },
} satisfies SxProps<Theme>
const SEARCH_ICON_SX = { color: "var(--app-text-secondary)", fontSize: "1.125rem" } satisfies SxProps<Theme>
// Long enough that a typed word settles into one event, short enough to land before the user moves on.
const SEARCH_SETTLE_MS = 800
const NO_MATCH_SX = { px: 2, py: 1.5, color: "var(--app-text-secondary)", fontSize: "0.875rem" } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>

export interface ChatSidePanelProps {
  threads: Thread[]
  activeThreadId: string | null
  canPinThreads: boolean
  /** Which conversations list this is; stamped on every event it emits. */
  surface: ThreadListSurface
  /** False until the thread list reaches a terminal state (success OR error) — drives the loading row. */
  isThreadsLoaded: boolean
  isThreadsErrored: boolean
  /** Id of the thread with a mutation in flight, or null — each row scopes its own busy state from it. */
  mutatingThreadId: string | null
  callbacks: ThreadRowCallbacks
  onStartNewThread: () => void
  /** Collapse the panel — the control lives at the panel's top-right (APP-3397). */
  onCollapse: () => void
  /** Focus target so a collapse→open→collapse cycle lands focus back here, never on <body>. */
  collapseButtonRef?: Ref<HTMLButtonElement>
}

interface ThreadSectionBaseProps {
  headingId: string
  label: string
  threads: Thread[]
  /** Unfiltered section size — the header count is a list size, not a match count, so it stays stable while filtering. */
  count: number
  activeThreadId: string | null
  canPinThreads?: boolean
  surface: ThreadListSurface
  mutatingThreadId?: string | null
  callbacks: ThreadRowCallbacks
  /** Reported with Thread Opened, which is tracked here because only the section knows which list a row sits in. */
  source: ThreadOpenSource
  /** "preview" renders select-only rows for the dock peek (no kebab/rename/pin/delete); "default" is the full docked row (APP-3397). */
  variant?: "default" | "preview"
  /** A search query is active: auto-reveal a collapsed section's matches (they'd otherwise hide with no feedback). */
  isSearching?: boolean
}

/**
 * The collapse toggle is all-or-nothing: a section either renders a toggle (both fields) or a static subheader
 * (neither). Pairing them as a discriminated union makes a half-supplied pair (which the render keys on
 * onToggleCollapse) a type error rather than a silent misconfiguration (APP-3404 T4).
 */
type ThreadSectionCollapseProps =
  | { isCollapsed: boolean, onToggleCollapse: () => void }
  | { isCollapsed?: never, onToggleCollapse?: never }

type ThreadSectionProps = ThreadSectionBaseProps & ThreadSectionCollapseProps

interface NewChatButtonProps {
  isActive: boolean
  onClick: () => void
}

function NewChatButton({ isActive, onClick }: NewChatButtonProps) {
  return (
    <Button
      sx={isActive ? NEW_CHAT_ACTIVE_SX : NEW_CHAT_SX}
      variant="text"
      startIcon={<Add />}
      onClick={onClick}
      data-testid="chatbox-new-thread"
    >
      New chat
    </Button>
  )
}

interface ThreadSearchFieldProps {
  value: string
  onChange: (value: string) => void
}

// Escape clears the query while the input is focused; when empty it's a no-op and stays out of the way.
function ThreadSearchField({ value, onChange }: ThreadSearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Clearing empties the value, which unmounts the Clear button — keep focus on the input so keyboard users
  // (and a mouse click on Clear) never drop to <body> mid-search.
  const clear = useCallback(() => {
    onChange("")
    inputRef.current?.focus()
  }, [onChange])
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Only consume Escape when it actually clears a non-empty query — this is the bottom rung of the dock's
    // Escape ladder, and the ladder only works because each rung claims the keystroke ONLY when it has
    // something to do with it. An empty-field Escape bubbles untouched to the artifact region and then the
    // dock, which closes on it; scoping stopPropagation to the clear is what stops one keystroke both
    // emptying the search and closing the dock.
    if (event.key === "Escape" && value) {
      event.preventDefault()
      event.stopPropagation()
      clear()
    }
  }
  return (
    <Box sx={SEARCH_WRAP_SX}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search conversations…"
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        sx={SEARCH_FIELD_SX}
        slotProps={{
          htmlInput: { "aria-label": "Search conversations", "data-testid": "chatbox-search" },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={SEARCH_ICON_SX} />
              </InputAdornment>
            ),
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" aria-label="Clear search" onClick={clear}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </Box>
  )
}

// aria-labelledby ties the rows to their heading for AT grouping. Exported so the dock preview reuses the
// same section markup (subheader + rows) with variant="preview" rather than duplicating it. Passing onToggleCollapse
// swaps the static subheader for a collapse toggle (docked panel, APP-3404); the count stays unfiltered either way.
export function ThreadSection({ headingId, label, threads, count, activeThreadId, canPinThreads, mutatingThreadId, callbacks, source, surface, variant = "default", isSearching = false, isCollapsed, onToggleCollapse }: ThreadSectionProps) {
  const analytics = useAnalytics()
  const handleSelect = useCallback((threadId: string) => {
    // Rows fire onSelect whether or not they are the open one, and navigateToThread returns early on the
    // active id — so tracking every click would count re-clicks that navigate nowhere as opens.
    if (threadId !== activeThreadId) analytics.track(AI_TEAMMATE_EVENTS.THREAD_OPENED, { source, surface, threadId })
    callbacks.onSelect(threadId)
  }, [analytics, callbacks, source, surface, activeThreadId])
  /**
   * The three mutations, wrapped here rather than inside the row: the row is memoized per thread and owns no
   * analytics, and this is already where the select is wrapped — so there is one place a future row action has
   * to be added to. `pinned` reports the state AFTER the toggle, so pin and unpin are one series.
   */
  const trackedCallbacks = useMemo<ThreadRowCallbacks>(() => {
    // Bound to consts so the null check narrows into the wrapper — the callback cannot be absent at call time.
    const { onRename, onPin, onDelete } = callbacks
    return {
      ...callbacks,
      onSelect: handleSelect,
      onRename: onRename == null ? undefined : (threadId: string, title: string) => {
        analytics.track(AI_TEAMMATE_EVENTS.THREAD_RENAMED, { threadId, surface })
        onRename(threadId, title)
      },
      onPin: onPin == null ? undefined : (threadId: string) => {
        // The target comes back FROM the toggle, which decides it against the cache. Deriving it here from the
        // rendered `threads` made two deciders of one fact: a rapid re-click reports off a row React has not
        // re-rendered yet, so the event says "pinned" for a click that unpinned. It also kept `threads` in this
        // memo's deps, so pinning one row rebuilt every row's callbacks.
        const pinned = onPin(threadId)
        analytics.track(AI_TEAMMATE_EVENTS.THREAD_PINNED, { threadId, pinned, surface })
        return pinned
      },
      onDelete: onDelete == null ? undefined : (threadId: string) => {
        analytics.track(AI_TEAMMATE_EVENTS.THREAD_DELETED, { threadId, surface })
        onDelete(threadId)
      },
    }
  }, [callbacks, handleSelect, analytics, surface])

  const rows = threads.map((thread) =>
    variant === "preview" ? (
      <ThreadListItemPreview
        key={thread.threadId}
        thread={thread}
        isActive={thread.threadId === activeThreadId}
        onSelect={handleSelect}
      />
    ) : (
      <ThreadListItem
        key={thread.threadId}
        thread={thread}
        isActive={thread.threadId === activeThreadId}
        canPinThreads={canPinThreads}
        isMutating={thread.threadId === mutatingThreadId}
        callbacks={trackedCallbacks}
      />
    ),
  )

  if (onToggleCollapse) {
    return (
      <Box component="section" aria-labelledby={headingId}>
        <Box
          component="button"
          type="button"
          id={headingId}
          onClick={onToggleCollapse}
          aria-expanded={!isCollapsed}
          data-testid={`chatbox-section-toggle-${label.toLowerCase()}`}
          sx={SECTION_TOGGLE_SX}
        >
          <span>{label} ({count})</span>
          <ExpandMore sx={{ ...CHEVRON_SX, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }} />
        </Box>
        <Collapse in={!isCollapsed || isSearching} unmountOnExit>
          <List dense disablePadding>{rows}</List>
        </Collapse>
      </Box>
    )
  }

  return (
    <List
      dense
      disablePadding
      aria-labelledby={headingId}
      subheader={<ListSubheader id={headingId} disableSticky sx={SUBHEADER_SX}>{label} ({count})</ListSubheader>}
    >
      {rows}
    </List>
  )
}

export function ChatSidePanel({
  threads,
  activeThreadId,
  canPinThreads,
  surface,
  isThreadsLoaded,
  isThreadsErrored,
  mutatingThreadId,
  callbacks,
  onStartNewThread,
  onCollapse,
  collapseButtonRef,
}: ChatSidePanelProps) {
  const [query, setQuery] = useState("")
  // Ephemeral per-section collapse (APP-3404): both expanded by default, not persisted (consistent with the panel's other local UI state).
  const [isPinnedCollapsed, setIsPinnedCollapsed] = useState(false)
  const [isRecentsCollapsed, setIsRecentsCollapsed] = useState(false)
  const analytics = useAnalytics()
  // Decided outside the updater — React may invoke an updater twice (StrictMode) and double-count the toggle.
  const togglePinned = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.THREAD_SECTION_TOGGLED, { section: "pinned", collapsed: !isPinnedCollapsed, surface })
    setIsPinnedCollapsed(!isPinnedCollapsed)
  }, [analytics, isPinnedCollapsed, surface])
  const toggleRecents = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.THREAD_SECTION_TOGGLED, { section: "recents", collapsed: !isRecentsCollapsed, surface })
    setIsRecentsCollapsed(!isRecentsCollapsed)
  }, [analytics, isRecentsCollapsed, surface])
  const { pinned, recents } = useMemo(() => partitionThreads(threads), [threads])
  const filteredPinned = useMemo(() => filterThreadsByQuery(pinned, query), [pinned, query])
  const filteredRecents = useMemo(() => filterThreadsByQuery(recents, query), [recents, query])
  const headingId = useId()

  const isSearching = query.trim().length > 0
  // Only a genuine no-match once the list has actually loaded without error — not while the spinner/alert shows.
  const hasNoMatch = isSearching && isThreadsLoaded && !isThreadsErrored && filteredPinned.length === 0 && filteredRecents.length === 0

  /**
   * One event per settled query, not per keystroke: typing "performance" would otherwise be eleven events, and
   * the interesting fact is whether the search the user stopped on found anything. The query text is NOT sent —
   * conversation titles are client data.
   */
  const hasMatch = filteredPinned.length > 0 || filteredRecents.length > 0
  // Read at fire time, not depended on: a background thread-list refresh (the React Query invalidation after every
  // pin) changes `hasMatch`, and `analytics` changes on every navigation — either as a dep re-arms the timer and
  // reports the same query twice with no keystroke.
  const hasMatchRef = useLatest(hasMatch)
  const analyticsRef = useLatest(analytics)
  useEffect(() => {
    // Nothing to report over a spinner or an error: `hasMatch` would be a no-match the user never got, and the
    // real answer arrives with the list. Loading gates the arming, so the settled query still reports once.
    if (!isSearching || !isThreadsLoaded || isThreadsErrored) return undefined
    const settle = setTimeout(() => analyticsRef.current.track(AI_TEAMMATE_EVENTS.THREADS_SEARCHED, { hasMatch: hasMatchRef.current, surface }), SEARCH_SETTLE_MS)
    return () => clearTimeout(settle)
    // `surface` is a dep, not a ref read: it is fixed for a mount, so it cannot re-arm the timer.
  }, [isSearching, query, isThreadsLoaded, isThreadsErrored, analyticsRef, hasMatchRef, surface])

  return (
    <Paper square elevation={0} sx={PANEL_SX} component="aside" aria-label="Conversations panel">
      <Box sx={COLLAPSE_ROW_SX}>
        <Tooltip title="Collapse conversations">
          <IconButton
            ref={collapseButtonRef}
            aria-label="Collapse conversations panel"
            data-testid="chatbox-collapse-panel"
            onClick={onCollapse}
          >
            <KeyboardDoubleArrowLeft />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={CONTENT_SX}>
        <NewChatButton isActive={activeThreadId === null} onClick={onStartNewThread} />
        <ThreadSearchField value={query} onChange={setQuery} />
        {isThreadsErrored && (
          <Alert severity="error" sx={ALERT_SX} data-testid="chatbox-threads-error">
            Couldn&apos;t load your conversations. Please try again later.
          </Alert>
        )}
        {!isThreadsLoaded && !isThreadsErrored && (
          <Box sx={LOADING_SX} data-testid="chatbox-threads-loading">
            <CircularProgress size={20} aria-label="Loading conversations" />
          </Box>
        )}
        <Box sx={LISTS_SCROLL_SX}>
          {filteredPinned.length > 0 && (
            <ThreadSection
              headingId={`${headingId}-pinned`}
              label="Pinned"
              source="pinned"
              threads={filteredPinned}
              count={pinned.length}
              activeThreadId={activeThreadId}
              canPinThreads={canPinThreads}
              surface={surface}
              mutatingThreadId={mutatingThreadId}
              callbacks={callbacks}
              isSearching={isSearching}
              isCollapsed={isPinnedCollapsed}
              onToggleCollapse={togglePinned}
            />
          )}
          {filteredRecents.length > 0 && (
            <ThreadSection
              headingId={`${headingId}-recents`}
              label="Recents"
              source="recents"
              threads={filteredRecents}
              count={recents.length}
              activeThreadId={activeThreadId}
              canPinThreads={canPinThreads}
              surface={surface}
              mutatingThreadId={mutatingThreadId}
              callbacks={callbacks}
              isSearching={isSearching}
              isCollapsed={isRecentsCollapsed}
              onToggleCollapse={toggleRecents}
            />
          )}
          {hasNoMatch && (
            <Typography sx={NO_MATCH_SX} data-testid="chatbox-search-no-match">
              No conversations match
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
